import type { MotionStyle } from './styles';

/**
 * Accents: moments in the middle of the video, tied to an element of the design (a hit on the
 * price, a marker under the headline…), so they land right on any template. Several can be
 * picked; one whose element the design lacks does nothing. Their emphasis replaces the layer
 * style's for that role.
 */
export const ACCENTS: MotionStyle[] = [
  {
    id: 'accent-price',
    category: 'accent',
    label: [
      'Price hit',
      'Удар по ціні',
      'Uderzenie w cenę',
      'Impact pe preț',
      'Golpe al precio',
      'Удар по цене',
    ],
    description: [
      'A burst and a camera punch as the price lands, then it beats',
      'Вибух і удар камери, коли зʼявляється ціна, потім вона пульсує',
      'Wybuch i uderzenie kamery, gdy pojawia się cena, potem pulsuje',
      'O explozie și un impact de cameră când apare prețul, apoi pulsează',
      'Un estallido y un golpe de cámara cuando llega el precio, luego late',
      'Взрыв и удар камеры, когда появляется цена, потом она пульсирует',
    ],
    duration: 0,
    stagger: 0,
    roles: { price: { emphasis: { preset: 'heartbeat', repeat: 2, gap: 300 } } },
    fx: [
      {
        kind: 'burst',
        after: 'price',
        offset: -200,
        duration: 700,
        anchor: 'price',
        params: { count: 14, size: 0.7 },
      },
    ],
    camera: [{ preset: 'punch', after: 'price', offset: -150, duration: 450 }],
  },
  {
    id: 'accent-underline',
    category: 'accent',
    label: [
      'Marker underline',
      'Підкреслення маркером',
      'Podkreślenie markerem',
      'Subliniere cu marker',
      'Subrayado a rotulador',
      'Подчёркивание маркером',
    ],
    description: [
      'A hand-drawn line under the headline once it is read',
      'Мальована лінія під заголовком, щойно його прочитали',
      'Odręczna linia pod nagłówkiem, gdy już go przeczytano',
      'O linie desenată sub titlu, după ce a fost citit',
      'Una línea a mano bajo el título cuando ya se ha leído',
      'Рисованная линия под заголовком, как только его прочитали',
    ],
    duration: 0,
    stagger: 0,
    fx: [{ kind: 'underline', after: 'headline', offset: 300, duration: 700, target: 'headline' }],
  },
  {
    id: 'accent-shine',
    category: 'accent',
    label: ['Light sweep', 'Відблиск', 'Odblask', 'Reflex', 'Destello', 'Блик'],
    description: [
      'A light sweep runs across the headline',
      'Відблиск пробігає по заголовку',
      'Odblask przebiega po nagłówku',
      'Un reflex trece peste titlu',
      'Un destello recorre el título',
      'Блик пробегает по заголовку',
    ],
    duration: 0,
    stagger: 0,
    fx: [{ kind: 'sweep', after: 'headline', offset: 400, duration: 1200, target: 'headline' }],
  },
  {
    id: 'accent-badge',
    category: 'accent',
    label: [
      'Badge pop',
      'Акцент на бейджі',
      'Akcent na plakietce',
      'Accent pe insignă',
      'Acento en la etiqueta',
      'Акцент на бейдже',
    ],
    description: [
      'Rings ripple out from the badge, it jumps and shakes the frame',
      'Від бейджа розходяться кільця, він підстрибує і струшує кадр',
      'Od plakietki rozchodzą się pierścienie, podskakuje i trzęsie kadrem',
      'Din insignă pornesc inele, sare și scutură cadrul',
      'Salen anillos de la etiqueta, salta y sacude el plano',
      'От бейджа расходятся кольца, он подпрыгивает и встряхивает кадр',
    ],
    duration: 0,
    stagger: 0,
    roles: { badge: { emphasis: { preset: 'tada', repeat: 1 } } },
    fx: [
      {
        kind: 'rings',
        after: 'badge',
        offset: -100,
        duration: 1100,
        anchor: 'badge',
        params: { count: 2, size: 0.6 },
      },
    ],
    camera: [
      { preset: 'shake', after: 'badge', offset: -100, duration: 400, params: { intensity: 0.5 } },
    ],
  },
  {
    id: 'accent-cta',
    category: 'accent',
    label: [
      'Button call',
      'Заклик кнопки',
      'Wezwanie przycisku',
      'Apel buton',
      'Llamada del botón',
      'Призыв кнопки',
    ],
    description: [
      'The button glints every few seconds and sends out a ring',
      'Кнопка відблискує кожні кілька секунд і пускає кільце',
      'Przycisk błyska co kilka sekund i wysyła pierścień',
      'Butonul strălucește la câteva secunde și trimite un inel',
      'El botón brilla cada pocos segundos y lanza un anillo',
      'Кнопка бликует каждые несколько секунд и пускает кольцо',
    ],
    duration: 0,
    stagger: 0,
    roles: { cta: { emphasis: { preset: 'shine', repeat: 0, gap: 1600 } } },
    fx: [
      {
        kind: 'rings',
        after: 'cta',
        offset: 200,
        duration: 1200,
        anchor: 'cta',
        params: { count: 2, size: 0.5 },
      },
    ],
  },
];
